// JQ4-38 · US-PAGOS-004 — Cash payment dialog (WU-4)
//
// Amount-entry dialog for registering a cash payment.
// - QA-oct I8: the operator types DOLLARS ("10.00"), converted to integer cents by string
//   parsing (no float math). The amount due (server-computed) is shown and prefilled.
// - Client-side guard: amount must be > 0 and, when the amount due is known, equal to it
//   (the server charges its computed total and ignores the typed amount; UX only).
// - On confirm: calls onConfirm(amountCents) - backend contract unchanged; error display and open/close are parent-controlled.
// - On server error: parent sets errorMessage; dialog stays open until user fixes input or cancels.

import { getTicketAmountDue } from '@jq4race/api-client';
import { useTranslation } from '@jq4race/i18n';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  formatCents,
} from '@jq4race/ui';
import { useEffect, useState } from 'react';

import { centsToInputValue, parseMoneyInputToCents } from '../../domain/money-input';

interface CashPaymentDialogProps {
  open: boolean;
  ticketNumber: number;
  plate: string;
  /** When set, the server-computed amount due is fetched, shown and prefilled. */
  ticketId?: string;
  /** Called with parsed amountCents when the operator confirms */
  onConfirm: (amountCents: number) => void;
  onCancel: () => void;
  /** Server-side error to surface inside the dialog */
  errorMessage?: string | null;
  /** Disables buttons while the mutation is in-flight */
  submitting?: boolean;
}

export function CashPaymentDialog({
  open,
  ticketNumber,
  plate,
  ticketId,
  onConfirm,
  onCancel,
  errorMessage,
  submitting = false,
}: CashPaymentDialogProps) {
  const { t } = useTranslation('pickup-queue');
  const [raw, setRaw] = useState('');
  const [due, setDue] = useState<{ cents: number; currency: string } | null>(null);

  // Best-effort: if the lookup fails (offline, 4xx) the operator can still type the amount.
  useEffect(() => {
    if (!open || !ticketId) return;
    let cancelled = false;
    setDue(null);
    getTicketAmountDue(ticketId)
      .then((res) => {
        if (cancelled) return;
        setDue({ cents: res.amountDueCents, currency: res.currency });
        if (res.amountDueCents > 0) {
          // Prefill only if the operator has not started typing.
          setRaw((current) => (current === '' ? centsToInputValue(res.amountDueCents) : current));
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [open, ticketId]);

  const amountCents = parseMoneyInputToCents(raw);
  // The server charges its own computed total and ignores the typed amount, so when the amount
  // due is known (and positive) the typed amount MUST equal it; otherwise the operator would
  // record a cash payment for a different figure than the one actually charged.
  const mismatch =
    due !== null && due.cents > 0 && amountCents !== null && amountCents !== due.cents;
  const isValid = amountCents !== null && !mismatch;

  function handleConfirm() {
    if (amountCents === null) return;
    onConfirm(amountCents);
  }

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      setRaw('');
      setDue(null);
      onCancel();
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('cashDialog.title')}</DialogTitle>
          <DialogDescription>
            {t('cashDialog.description', { plate, ticketNumber })}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3 py-2">
          {due ? (
            <p className="font-sans text-sm text-foreground">
              {t('cashDialog.amountDue', { amount: formatCents(due.cents, due.currency) })}
            </p>
          ) : null}
          <label className="flex flex-col gap-1.5">
            <span className="font-sans text-sm font-medium text-foreground">
              {t('cashDialog.amountLabel')}
            </span>
            <input
              type="text"
              inputMode="decimal"
              autoComplete="off"
              value={raw}
              onChange={(e) => setRaw(e.target.value)}
              placeholder={t('cashDialog.amountPlaceholder')}
              aria-label={t('cashDialog.amountAria')}
              className="rounded-sm border border-input bg-background px-3 py-2 font-sans text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
            />
          </label>

          {mismatch ? (
            <p role="status" className="font-sans text-sm text-warning">
              {t('cashDialog.amountMismatch')}
            </p>
          ) : null}

          {errorMessage ? (
            <p role="alert" className="font-sans text-sm text-destructive">
              {errorMessage}
            </p>
          ) : null}
        </div>

        <DialogFooter>
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="rounded-sm px-4 py-2 text-sm text-muted-foreground hover:text-foreground disabled:opacity-60"
          >
            {t('common:actions.cancel')}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!isValid || submitting}
            aria-label={t('cashDialog.confirmAria')}
            className="rounded-sm bg-warning px-4 py-2 text-sm font-medium text-warning-foreground disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? t('cashDialog.registering') : t('common:actions.confirm')}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
